import { Component } from '@theme/component';
import { API_ENDPOINTS } from '@theme/api-endpoints';
import { isMobileAppWebView } from '@theme/utilities';

/**
 * @typedef {object} Refs
 * @property {import('./dialog.js').default} dialogComponent - The dialog-component wrapper.
 * @property {HTMLElement} detectedSection - Hidden section revealed when mismatch is found.
 * @property {HTMLElement} detectedFlag - Container for the detected country's flag HTML.
 * @property {HTMLElement} detectedIso - Element showing the detected country ISO code.
 * @property {HTMLElement} detectedText - "Proceed to shopping at X store?" text element.
 * @property {HTMLAnchorElement} switchLink - The CTA link to switch storefronts.
 */

const SESSION_KEY = 'ma_geo_mismatch_dismissed';
const COOKIE_NAME = 'ma_geo_info';
const COOKIE_MAX_AGE_S = 3600; // 1 hour

/** @type {Promise<object | null> | null} */
let geoInfoPromise = null;

/**
 * Fetch the geo-info endpoint once and share the result across instances.
 * Any failure resolves to null — the popup simply doesn't appear.
 *
 * @returns {Promise<object | null>}
 */
function getGeoInfo() {
  if (!geoInfoPromise) {
    const endpoint = API_ENDPOINTS.cfGeoInfo || '';
    geoInfoPromise = endpoint
      ? fetch(endpoint)
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null)
      : Promise.resolve(null);
  }
  return geoInfoPromise;
}

/**
 * Read a cookie value by name.
 * @param {string} name
 * @returns {string | null}
 */
function getCookie(name) {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Write a JSON cookie with a max-age (seconds).
 * @param {string} name
 * @param {object} value
 * @param {number} maxAge
 */
function setCookie(name, value, maxAge) {
  document.cookie =
    name +
    '=' +
    encodeURIComponent(JSON.stringify(value)) +
    '; path=/; SameSite=Lax; max-age=' +
    maxAge;
}

/**
 * Country Mismatch Popup — geo-conditional dialog.
 *
 * Detects when a visitor's IP-based country differs from the current
 * storefront's country and offers a one-click switch. Dismissed for the
 * remainder of the browser session.
 *
 * @extends {Component<Refs>}
 */
class CountryMismatchPopup extends Component {
  requiredRefs = ['dialogComponent'];

  /**
   * Geo data resolved this page load. Persisted to the cookie only once the
   * visitor acts on the popup (stay / switch / close), not on fetch.
   * @type {object | null}
   */
  #geoData = null;

  connectedCallback() {
    super.connectedCallback();
    this.#init();
  }

  /**
   * Handle "Stay on X site" click.
   */
  handleStay() {
    this.#suppress();
    this.refs.dialogComponent.closeDialog();
  }

  async #init() {
    // 0. Webview guard
    //
    // Inside the MA app's webview the app owns country selection, so the popup is
    // suppressed outright. Bailing here — ahead of the geo lookup — also avoids a
    // pointless call to the geo-info endpoint. Nothing is visible at this point:
    // the dialog is only ever opened at the end of this method, so returning early
    // leaves it in its shipped hidden state with no flash.
    // snippets/mobile-app-webview.liquid sets the class in <head>, so it is already
    // on the document by the time this module runs.
    if (isMobileAppWebView()) return;

    // 1. Session guard
    try {
      if (sessionStorage.getItem(SESSION_KEY)) return;
    } catch {
      // sessionStorage unavailable — proceed without guard
    }

    // 2. Resolve geo data from cookie or endpoint
    let geoData = null;

    const raw = getCookie(COOKIE_NAME);
    if (raw) {
      try {
        geoData = JSON.parse(raw);
      } catch {
        geoData = null;
      }
    }

    if (!geoData) {
      geoData = await getGeoInfo();
    }

    if (!geoData || !geoData.country) return;

    // Hold the resolved geo data; it is only written to the cookie once the
    // visitor acts on the popup (see #suppress).
    this.#geoData = geoData;

    // 3. Compare geo country vs storefront country
    const detectedIso = geoData.country.toUpperCase();
    const storefrontCountry =
      (typeof Theme !== 'undefined' && Theme.locale?.country) || '';

    if (detectedIso === storefrontCountry.toUpperCase()) return;

    // 4. Find matching storefront entry in server-rendered data
    const entry = this.querySelector(`template[data-iso="${detectedIso}"]`);
    if (!entry) return;

    const entryName = entry.getAttribute('data-name') || '';
    const entryUrl = entry.getAttribute('data-url') || '';
    const entryFlagHtml = entry.getAttribute('data-flag-html') || '';

    if (!entryUrl) return;

    // 5. Populate detected section
    //
    // These elements live inside the nested <dialog-component>, so the
    // framework's ref system assigns them to that component (refs belong to the
    // closest ancestor Component) — not to this one. Query them directly rather
    // than reading this.refs, which would be undefined here.
    const detectedSection = this.querySelector('[ref="detectedSection"]');
    const detectedFlag = this.querySelector('[ref="detectedFlag"]');
    const isoEl = this.querySelector('[ref="detectedIso"]');
    const detectedText = this.querySelector('[ref="detectedText"]');
    const switchLink = /** @type {HTMLAnchorElement | null} */ (this.querySelector('[ref="switchLink"]'));

    if (detectedFlag) detectedFlag.innerHTML = entryFlagHtml;
    if (isoEl) isoEl.textContent = detectedIso;

    // Replace __COUNTRY__ placeholder in the proceed template
    const proceedTemplate = this.getAttribute('data-proceed-template') || '';
    if (detectedText) {
      detectedText.textContent = proceedTemplate.replace('__COUNTRY__', entryName);
    }

    // Populate switch link
    const switchTemplate = this.getAttribute('data-switch-template') || '';
    if (switchLink) {
      // Navigate to origin (homepage) of the detected storefront
      try {
        const url = new URL(entryUrl);
        switchLink.href = url.origin;
      } catch {
        switchLink.href = entryUrl;
      }
      switchLink.textContent = switchTemplate.replace('__STORE__', entryName);
    }

    // Reveal the detected section
    if (detectedSection) detectedSection.removeAttribute('hidden');

    // 6. Show the dialog
    this.refs.dialogComponent.showDialog();

    // 7. Listen for dialog:close (handles Escape key and backdrop click)
    this.refs.dialogComponent.addEventListener('dialog:close', () => {
      this.#suppress();
    }, { once: true });
  }

  /**
   * Called when the visitor acts on the popup (stay / switch / close).
   * Suppresses the popup for the rest of the session and persists the geo
   * result to the short-lived cookie so it isn't re-fetched for ~1 hour.
   */
  #suppress() {
    try {
      sessionStorage.setItem(SESSION_KEY, '1');
    } catch {
      // sessionStorage unavailable — best-effort
    }

    if (this.#geoData) {
      setCookie(COOKIE_NAME, this.#geoData, COOKIE_MAX_AGE_S);
    }
  }
}

if (!customElements.get('country-mismatch-popup')) {
  customElements.define('country-mismatch-popup', CountryMismatchPopup);
}
